<script setup>
import { computed, ref } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from './provider.js';
import { hasOneDayPassed } from 'shared/helpers/timeHelper';
import { ORIENTATION, MESSAGE_STATUS } from './constants';

defineProps({
  error: { type: String, required: true },
});

const emit = defineEmits(['retry']);

const { orientation, status, createdAt, content, attachments } =
  useMessageContext();

const { t } = useI18n();

// BonBoZ: trên màn cảm ứng không rê chuột được, nên bấm vào dấu cảnh báo để
// bật/tắt chi tiết lỗi. Trước đây chỉ có `group-hover`, mà `:hover` trên cảm
// ứng thì DÍNH LẠI sau khi chạm — hộp chi tiết bật ra rồi ở lì, đè lên tin
// phía trên (báo lỗi 23/09/2026).
const hienChiTiet = ref(false);

const canRetry = computed(() => {
  const hasContent = content.value !== null;
  const hasAttachments = attachments.value && attachments.value.length > 0;
  return !hasOneDayPassed(createdAt.value) && (hasContent || hasAttachments);
});
</script>

<template>
  <div class="text-xs text-n-ruby-11 flex items-center gap-1.5">
    <span>{{ t('CHAT_LIST.FAILED_TO_SEND') }}</span>
    <div class="relative group">
      <button
        type="button"
        :aria-expanded="hienChiTiet"
        :aria-label="t('CHAT_LIST.FAILED_TO_SEND')"
        class="grid place-content-center rounded-md cursor-pointer size-5 bg-n-alpha-2"
        @click="hienChiTiet = !hienChiTiet"
      >
        <Icon
          icon="i-lucide-alert-triangle"
          class="text-n-ruby-11 size-[14px]"
        />
      </button>
      <div
        class="bonboz-chi-tiet-loi absolute z-20 px-3 py-2 text-xs border rounded-xl bottom-6 w-56 max-h-32 overflow-y-auto overscroll-contain break-words opacity-0 invisible transition-all bg-n-alpha-3 border-n-strong text-n-slate-12 backdrop-blur-[100px] shadow-[0px_0px_24px_0px_rgba(0,0,0,0.12)]"
        :class="[
          hienChiTiet ? 'opacity-100 visible' : '',
          orientation === ORIENTATION.LEFT
            ? 'ltr:left-0 rtl:right-0'
            : 'ltr:right-0 rtl:left-0',
        ]"
      >
        {{ error }}
      </div>
    </div>
    <button
      v-if="canRetry"
      type="button"
      :disabled="status !== MESSAGE_STATUS.FAILED"
      class="bg-n-alpha-2 rounded-md size-5 grid place-content-center cursor-pointer"
      @click="emit('retry')"
    >
      <Icon icon="i-lucide-refresh-ccw" class="text-n-ruby-11 size-[14px]" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
// Rê chuột vẫn hiện như cũ — nhưng CHỈ trên thiết bị có chuột thật. Viết bằng
// CSS chứ không bằng lớp `group-hover:` của Tailwind, vì Tailwind không cho bọc
// biến thể đó trong media query.
@media (hover: hover) {
  .group:hover .bonboz-chi-tiet-loi {
    @apply visible opacity-100;
  }
}
</style>
